/**
 * `3f` — Novo padrão de conformidade (modal de `3a`).
 *
 * Fluxo ao enviar: `POST /compliance/padroes` → para CADA manual: upload-url → PUT
 * no S3 → `POST /documentos/manuais`. Um manual que falha **não desfaz o padrão**:
 * o padrão já existe, e a tela lista o arquivo que não subiu em vez de fingir que
 * nada aconteceu.
 *
 * ⚠️ assumido — §9: o contrato pede `ano` no POST e o shot só desenha "nome do
 * padrão". Quando o nome é um ano de 4 dígitos (o caso do shot, "2027"), o ano sai
 * dele; senão, aparece um campo "Ano" obrigatório.
 */
import { useState } from 'react'

import type { Padrao } from '@/api/contracts/compliance'
import { enviarManual, useCriarPadrao, useEstrutura } from '@/api/queries/compliance'
import { tipoDoArquivo } from '@/api/upload'
import { detalhesDoErro, mensagemDoErro } from '@/components/erros'
import { Botao, Campo, Entrada } from '@/components/primitivos'
import { ZonaDeArquivo } from '@/components/ZonaDeArquivo'

type Origem = 'importado' | 'zero'

export function NovoPadraoModal({
  padroes,
  aoFechar,
  aoCriar,
}: {
  padroes: readonly Padrao[]
  aoFechar: () => void
  aoCriar: (idPadrao: string) => void
}) {
  const anterior = padroes[0]
  const estruturaAnterior = useEstrutura(anterior?.id)
  const criar = useCriarPadrao()

  const [nome, setNome] = useState('')
  const [anoDigitado, setAnoDigitado] = useState('')
  const [origem, setOrigem] = useState<Origem>(anterior ? 'importado' : 'zero')
  const [manuais, setManuais] = useState<File[]>([])
  const [recusados, setRecusados] = useState<string[]>([])
  const [tentou, setTentou] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [criado, setCriado] = useState<{ id: string; falhas: Array<{ nome: string; erro: string }> } | null>(null)

  const nomeLimpo = nome.trim()
  const anoDoNome = /^\d{4}$/.test(nomeLimpo) ? Number(nomeLimpo) : null
  const ano = anoDoNome ?? (/^\d{4}$/.test(anoDigitado.trim()) ? Number(anoDigitado.trim()) : null)
  const faltando = [...(nomeLimpo ? [] : ['Nome do padrão']), ...(ano === null && nomeLimpo ? ['Ano'] : [])]

  function adicionar(arquivos: File[]) {
    const aceitos = arquivos.filter((a) => tipoDoArquivo(a))
    setRecusados(arquivos.filter((a) => !tipoDoArquivo(a)).map((a) => a.name))
    setManuais((atuais) => [...atuais, ...aceitos.filter((a) => !atuais.some((x) => x.name === a.name))])
  }

  async function enviar() {
    setTentou(true)
    if (faltando.length > 0 || ano === null) return
    setEnviando(true)
    try {
      const padrao = await criar.mutateAsync({
        nome: nomeLimpo,
        ano,
        origem: origem === 'importado' && anterior ? { tipo: 'importado', padraoOrigemId: anterior.id } : { tipo: 'zero' },
      })
      const falhas: Array<{ nome: string; erro: string }> = []
      for (const arquivo of manuais) {
        try {
          await enviarManual(padrao.id, arquivo)
        } catch (erro) {
          falhas.push({ nome: arquivo.name, erro: mensagemDoErro(erro) })
        }
      }
      if (falhas.length === 0) aoCriar(padrao.id)
      else setCriado({ id: padrao.id, falhas })
    } catch {
      // O erro do POST fica em `criar.error` e é mostrado abaixo.
    } finally {
      setEnviando(false)
    }
  }

  const camposDoServidor = (detalhesDoErro(criar.error).camposFaltantes as string[] | undefined) ?? []

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Novo padrão de conformidade"
      className="bg-iox-black/40 fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget && !enviando) aoFechar()
      }}
    >
      <div className="bg-surface-card rounded-panel shadow-panel flex max-h-full w-full max-w-[720px] flex-col gap-5 overflow-y-auto p-8">
        <div className="flex items-start justify-between gap-4">
          <h2 className="type-h3 text-text-primary">Novo padrão de conformidade</h2>
          <button
            type="button"
            aria-label="Fechar"
            disabled={enviando}
            onClick={() => (criado ? aoCriar(criado.id) : aoFechar())}
            className="text-icon-default hover:text-iox-teal duration-fast"
          >
            <i className="ph ph-x" aria-hidden="true" />
          </button>
        </div>

        {criado ? (
          <div className="flex flex-col gap-3">
            <p role="status" className="type-body text-text-body">
              Padrão <strong>{nomeLimpo}</strong> criado. {criado.falhas.length === 1 ? 'Um manual não foi enviado' : `${criado.falhas.length} manuais não foram enviados`} — envie de novo quando possível:
            </p>
            <ul className="flex flex-col gap-1">
              {criado.falhas.map((f) => (
                <li key={f.nome} className="type-sm text-danger-700">
                  {f.nome}: {f.erro}
                </li>
              ))}
            </ul>
            <Botao variante="primaria" className="self-end" onClick={() => aoCriar(criado.id)}>
              ir para o padrão {nomeLimpo}
            </Botao>
          </div>
        ) : (
          <>
            <Campo
              rotulo="Nome do padrão"
              obrigatorio
              hint="identifica o padrão nos seletores das outras abas"
              erro={tentou && !nomeLimpo ? 'Informe o nome do padrão.' : undefined}
            >
              <Entrada value={nome} placeholder="2027" onChange={(e) => setNome(e.target.value)} />
            </Campo>

            {nomeLimpo && anoDoNome === null && (
              <Campo
                rotulo="Ano"
                obrigatorio
                hint="o nome não é um ano — informe o ano do padrão"
                erro={tentou && ano === null ? 'Informe o ano com 4 dígitos.' : undefined}
              >
                <Entrada inputMode="numeric" value={anoDigitado} onChange={(e) => setAnoDigitado(e.target.value)} />
              </Campo>
            )}

            <fieldset className="flex flex-col gap-3">
              <legend className="type-eyebrow text-text-muted mb-2">
                Como começar <span className="text-danger-700">*</span>
              </legend>
              <CartaoDeOrigem
                valor="importado"
                selecionado={origem === 'importado'}
                desabilitado={!anterior}
                aoSelecionar={setOrigem}
                icone="copy"
                titulo={anterior ? `Importar o padrão anterior (${anterior.nome})` : 'Importar o padrão anterior'}
                descricao={
                  anterior
                    ? `Carrega ${
                        estruturaAnterior.data ? `os ${estruturaAnterior.data.perguntas.length} itens` : 'os itens'
                      }, as opções de resposta e as pontuações como ponto de partida. Tudo fica editável depois — perguntas podem ser alteradas, criadas ou removidas.`
                    : 'Não há padrão anterior para importar.'
                }
              />
              <CartaoDeOrigem
                valor="zero"
                selecionado={origem === 'zero'}
                aoSelecionar={setOrigem}
                icone="file-plus"
                titulo="Criar do zero"
                descricao="Padrão vazio, sem grupos nem perguntas — cadastrados um a um."
              />
            </fieldset>

            <div className="flex flex-col gap-2">
              <span className="type-eyebrow text-text-muted">Manuais &amp; resoluções de referência</span>
              <ZonaDeArquivo
                titulo="arraste os arquivos ou selecione"
                descricao="um ou mais documentos · PDF ou DOCX"
                icone="books"
                rotulo="Manuais e resoluções de referência"
                multiplo
                aoEscolher={adicionar}
              />
              {manuais.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {manuais.map((m) => (
                    <li key={m.name} className="rounded-control bg-gray-100 text-text-body inline-flex items-center gap-2 px-3 py-1.5 type-sm">
                      <i className="ph ph-file-text" aria-hidden="true" />
                      {m.name}
                      <button
                        type="button"
                        aria-label={`remover ${m.name}`}
                        onClick={() => setManuais((atuais) => atuais.filter((x) => x !== m))}
                      >
                        <i className="ph ph-x" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {recusados.length > 0 && (
                <p className="type-sm text-danger-700">Só PDF ou DOCX — ignorados: {recusados.join(', ')}.</p>
              )}
              <p className="type-sm text-text-muted">
                Servem de referência para a IA ao dar o veredito de conformidade dos documentos enviados pelos
                clientes, junto com a orientação cadastrada em cada pergunta.
              </p>
            </div>

            <p className="rounded-card border-border-subtle bg-subtle text-text-body flex gap-3 border p-4 type-sm">
              <i className="ph ph-sparkle text-iox-teal mt-0.5" aria-hidden="true" />
              <span>
                Ao importar, a orientação para a IA de cada pergunta vem junto. As{' '}
                <strong>evidências dos clientes não são copiadas</strong> — o reaproveitamento acontece por
                cliente, no assistente de novo padrão.
              </span>
            </p>

            {tentou && faltando.length > 0 && (
              <p role="alert" className="type-sm text-danger-700">
                Preencha: {faltando.join(', ')}.
              </p>
            )}
            {criar.isError && (
              <p role="alert" className="type-sm text-danger-700">
                {mensagemDoErro(criar.error)}
                {camposDoServidor.length > 0 && ` Campos: ${camposDoServidor.join(', ')}.`}
              </p>
            )}

            <div className="flex justify-end gap-3">
              <Botao onClick={aoFechar} disabled={enviando}>
                cancelar
              </Botao>
              <Botao variante="primaria" disabled={enviando} onClick={() => void enviar()}>
                {enviando ? 'criando…' : `criar padrão${nomeLimpo ? ` ${nomeLimpo}` : ''}`}
              </Botao>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function CartaoDeOrigem({
  valor,
  selecionado,
  desabilitado = false,
  aoSelecionar,
  icone,
  titulo,
  descricao,
}: {
  valor: Origem
  selecionado: boolean
  desabilitado?: boolean
  aoSelecionar: (valor: Origem) => void
  icone: string
  titulo: string
  descricao: string
}) {
  return (
    <label
      className={`rounded-card flex gap-4 border p-5 duration-fast ${
        selecionado ? 'border-iox-navy bg-navy-50' : 'border-border-subtle hover:border-border-strong'
      } ${desabilitado ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      <input
        type="radio"
        name="como-comecar"
        className="sr-only"
        value={valor}
        checked={selecionado}
        disabled={desabilitado}
        onChange={() => aoSelecionar(valor)}
      />
      <i className={`ph ph-${icone} text-iox-navy text-2xl`} aria-hidden="true" />
      <span className="flex flex-col gap-1">
        <span className={`type-label ${selecionado ? 'text-iox-navy' : 'text-text-primary'}`}>{titulo}</span>
        <span className="type-sm text-text-muted">{descricao}</span>
      </span>
    </label>
  )
}
